import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CloudDownload, Plus, Send, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Select, Switch } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import type { TicketMeta } from '@/lib/types';

/*
 * WhatsApp-Vorlagen je Zweck (Ticket eröffnet je Tickettyp, Reparatur, abholbereit, Antwort > 24 h, intern).
 * Name und Sprache müssen genau der bei Meta freigegebenen Vorlage entsprechen; die Werte für {{1}}, {{2}} …
 * werden mit Platzhaltern aus dem Vorgang gefüllt.
 */

interface Template {
  id?: string;
  purpose: string;
  areaId: string | null;
  name: string;
  language: string;
  params: string[];
  active: boolean;
}
interface MetaTemplate {
  name: string;
  language: string;
  status: string;
  category: string;
  body: string;
  params: number;
}

const VARS: Record<string, string[]> = {
  ticket_created: ['{{ticket.nummer}}', '{{ticket.betreff}}', '{{ticket.bereich}}', '{{kunde.name}}', '{{ansprechpartner.name}}', '{{ansprechpartner.vorname}}', '{{firma.name}}'],
  repair_created: ['{{reparatur.nummer}}', '{{reparatur.geraet}}', '{{kunde.name}}', '{{ansprechpartner.name}}', '{{ansprechpartner.vorname}}', '{{firma.name}}'],
  repair_ready: ['{{reparatur.nummer}}', '{{reparatur.geraet}}', '{{kunde.name}}', '{{ansprechpartner.name}}', '{{ansprechpartner.vorname}}', '{{firma.name}}'],
  ticket_reply: ['{{text}}', '{{ticket.nummer}}', '{{ticket.betreff}}', '{{absender.name}}'],
  notify: ['{{text}}'],
};
const STATUS: Record<string, { label: string; variant: 'success' | 'outline' | 'destructive' }> = {
  APPROVED: { label: 'freigegeben', variant: 'success' },
  PENDING: { label: 'wird geprüft', variant: 'outline' },
  REJECTED: { label: 'abgelehnt', variant: 'destructive' },
  PAUSED: { label: 'pausiert', variant: 'destructive' },
};

export function WhatsAppTemplatesCard({ meta, hasWaba }: { meta: TicketMeta; hasWaba: boolean }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['whatsapp', 'templates'], queryFn: () => api<{ items: Template[]; purposes: { key: string; label: string }[]; languages: string[] }>('/integrations/whatsapp/templates') });
  const [items, setItems] = useState<Template[]>([]);
  const [metaList, setMetaList] = useState<MetaTemplate[] | null>(null);
  const [testPhone, setTestPhone] = useState('');
  useEffect(() => {
    if (data) setItems(data.items.map((t) => ({ ...t, areaId: t.areaId ?? null })));
  }, [data]);
  const loadMeta = useMutation({ mutationFn: () => api<{ items: MetaTemplate[] }>('/integrations/whatsapp/meta-templates'), onSuccess: (r) => setMetaList(r.items), onError: (e) => toast.error(errorMessage(e)) });
  const save = useMutation({
    mutationFn: () => api('/integrations/whatsapp/templates', { method: 'PUT', body: { items: items.map(({ id: _id, ...t }) => t) } }),
    onSuccess: () => {
      toast.success('Vorlagen gespeichert');
      qc.invalidateQueries({ queryKey: ['whatsapp', 'templates'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const test = useMutation({
    mutationFn: (t: Template) => api<{ ok: boolean; message: string }>('/integrations/whatsapp/templates/test', { method: 'POST', body: { phone: testPhone, name: t.name, language: t.language, params: t.params } }),
    onSuccess: (r) => (r.ok ? toast.success : toast.error)(r.message, { duration: 10_000 }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!data) return null;
  const set = (i: number, patch: Partial<Template>) => setItems((list) => list.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const metaFor = (t: Template) => metaList?.find((m) => m.name === t.name && m.language === t.language);
  const areas = meta.areas.filter((a) => a.active);

  return (
    <Card className="xl:col-span-2">
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Vorlagen (Nachrichten, die das Portal von sich aus sendet)</CardTitle>
          <CardDescription>
            Für „Ticket eröffnet“ (je Tickettyp), „Reparatur angenommen“, „abholbereit“, Antworten nach mehr als 24 Stunden und interne Benachrichtigungen. Name und Sprache genau wie bei Meta; Vorlagen müssen dort <strong>freigegeben</strong> sein. Kunden erhalten Nachrichten nur, wenn beim Ansprechpartner (bzw. Privatkunden) die WhatsApp-Einwilligung angehakt ist.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={() => loadMeta.mutate()} loading={loadMeta.isPending} disabled={!hasWaba} title={hasWaba ? undefined : 'WhatsApp-Business-Konto-ID eintragen und speichern'}>
          <CloudDownload /> Von Meta laden
        </Button>
      </CardHeader>
      <CardContent className="grid gap-3">
        {metaList && (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Vorlage bei Meta</th>
                  <th className="px-3 py-2 text-left font-medium">Sprache</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Platzhalter</th>
                  <th className="px-3 py-2 text-left font-medium">Text</th>
                </tr>
              </thead>
              <tbody>
                {metaList.map((m) => (
                  <tr key={`${m.name}-${m.language}`} className="border-t">
                    <td className="px-3 py-2 font-mono">{m.name}</td>
                    <td className="px-3 py-2 font-mono">{m.language}</td>
                    <td className="px-3 py-2">
                      <Badge variant={STATUS[m.status]?.variant ?? 'outline'}>{STATUS[m.status]?.label ?? m.status}</Badge>
                    </td>
                    <td className="px-3 py-2">{m.params}</td>
                    <td className="max-w-md truncate px-3 py-2 text-muted-foreground" title={m.body}>
                      {m.body}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {items.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Vorlage zugeordnet.</p>}
        {items.map((t, i) => {
          const m = metaFor(t);
          return (
            <div key={i} className="grid gap-2 rounded-md border p-3">
              <div className="grid gap-2 lg:grid-cols-[14rem_12rem_1fr_9rem_auto]">
                <Select value={t.purpose} onChange={(e) => set(i, { purpose: e.target.value, areaId: null, params: [] })} aria-label="Zweck">
                  {data.purposes.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </Select>
                {t.purpose === 'ticket_created' ? (
                  <Select value={t.areaId ?? ''} onChange={(e) => set(i, { areaId: e.target.value || null })} aria-label="Tickettyp">
                    <option value="">alle übrigen Tickettypen</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <span />
                )}
                <Input
                  value={t.name}
                  onChange={(e) => set(i, { name: e.target.value.trim().toLowerCase() })}
                  placeholder="Name der Vorlage bei Meta, z. B. ticket"
                  aria-label="Name der Vorlage"
                  className="font-mono"
                  list="wa-meta-names"
                />
                <Select value={t.language} onChange={(e) => set(i, { language: e.target.value })} aria-label="Sprache">
                  {data.languages.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </Select>
                <div className="flex items-center gap-2">
                  <Switch checked={t.active} onCheckedChange={(v) => set(i, { active: v })} aria-label="Aktiv" />
                  <Button type="button" variant="ghost" size="icon" className="size-8 text-destructive hover:text-destructive" onClick={() => setItems((l) => l.filter((_, j) => j !== i))} aria-label="Vorlage entfernen">
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
              {m && (
                <p className="text-xs text-muted-foreground">
                  Bei Meta: <Badge variant={STATUS[m.status]?.variant ?? 'outline'}>{STATUS[m.status]?.label ?? m.status}</Badge> · {m.params} Platzhalter · „{m.body}“
                  {m.params !== t.params.length && <span className="ml-1 text-destructive">– bitte {m.params} Werte angeben</span>}
                </p>
              )}
              {metaList && !m && t.name && <p className="text-xs text-destructive">Bei Meta gibt es „{t.name}“ in Sprache „{t.language}“ nicht.</p>}
              <div className="grid gap-2">
                {t.params.map((p, k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-10 shrink-0 text-right font-mono text-xs text-muted-foreground">{`{{${k + 1}}}`}</span>
                    <Input value={p} onChange={(e) => set(i, { params: t.params.map((x, j) => (j === k ? e.target.value : x)) })} aria-label={`Wert für Platzhalter ${k + 1}`} />
                    <Select value="" onChange={(e) => e.target.value && set(i, { params: t.params.map((x, j) => (j === k ? `${x}${e.target.value}` : x)) })} aria-label="Variable einfügen" className="w-44 shrink-0">
                      <option value="">+ Variable</option>
                      {(VARS[t.purpose] ?? []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Select>
                    <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0" onClick={() => set(i, { params: t.params.filter((_, j) => j !== k) })} aria-label={`Platzhalter ${k + 1} entfernen`}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-2">
                  {t.params.length < 10 && (
                    <Button type="button" variant="outline" size="sm" onClick={() => set(i, { params: [...t.params, (VARS[t.purpose] ?? [''])[0] ?? ''] })}>
                      <Plus /> Platzhalter {`{{${t.params.length + 1}}}`}
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="sm" disabled={testPhone.length < 6 || !t.name} onClick={() => test.mutate(t)} loading={test.isPending && test.variables === t}>
                    <Send /> Test an Nummer
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        <datalist id="wa-meta-names">
          {metaList?.map((m) => <option key={`${m.name}-${m.language}`} value={m.name} />)}
        </datalist>
        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={() => setItems((l) => [...l, { purpose: 'ticket_created', areaId: null, name: '', language: 'de', params: ['{{ticket.nummer}}'], active: true }])}>
            <Plus /> Vorlage zuordnen
          </Button>
          <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Testnummer +49 151 …" aria-label="Testnummer" className="w-48" />
          <Button className="ml-auto" onClick={() => save.mutate()} loading={save.isPending}>
            Vorlagen speichern
          </Button>
        </div>
        <Alert>
          Häufige Ursachen für „template name does not exist“: Vorlage noch in Prüfung, Name falsch geschrieben oder falsche Sprache (z. B. „en_US“ statt „us“). „Von Meta laden“ zeigt Name, Sprache, Status und Anzahl der Platzhalter.
        </Alert>
      </CardContent>
    </Card>
  );
}
